// import external libraries
import { useEffect, useState } from 'react';

// import hooks
import { useNavParams } from 'hooks';
import useSubNavigation from 'hooks/useSubNavigation';
import { queries } from 'RestAPIs/rapini';
import { useGetPatientDetails } from './queries';

// import types
import { CARE_RECEIVERS_SESSION_STORAGE_KEYS, TabType } from './types';
import type { CareReceiverHandover, Patient } from './types';

// import utils
import { careReceiverHandoverToPatient, isTabType } from './utils';

export { TabType };

export const useCareReceiversHome = () => {
  const { params } = useNavParams();

  // Seeded from the hash rather than defaulted, so a deep link such as
  // #roster,careReceivers&page=prescriptions opens on that tab. useSubNavigation skips its initial
  // mount, so without this the page would always start on the care receiver information tab.
  const [subPage, setSubPage] = useState<TabType>(() =>
    isTabType(params?.page) ? params.page : TabType.PATIENT_INFO
  );
  const { setPage } = useSubNavigation<TabType>(
    TabType.PATIENT_INFO,
    'page',
    setSubPage
  );

  const onTabChanged = (_, newValue: TabType) => {
    setPage(newValue, undefined, true);
  };

  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Apply a care receiver handed over via session storage once on mount, then clear it, so opening
  // this page later from the menu starts empty instead of resurrecting an earlier selection.
  useEffect(() => {
    const stored = window.sessionStorage.getItem(
      CARE_RECEIVERS_SESSION_STORAGE_KEYS.SELECTED_CARE_RECEIVER
    );

    if (!stored) return;

    window.sessionStorage.removeItem(
      CARE_RECEIVERS_SESSION_STORAGE_KEYS.SELECTED_CARE_RECEIVER
    );

    try {
      const handover = JSON.parse(stored) as CareReceiverHandover;

      if (handover?.careReceiverId) {
        setSelectedPatient(careReceiverHandoverToPatient(handover));
      }
    } catch {
      // Ignore malformed session storage payloads.
    }
  }, []);

  const onSelectPatient = (patient: Patient | null) => {
    setSelectedPatient(patient);
  };

  const { data: patientDetails, isLoading: isLoadingDetails } =
    useGetPatientDetails(selectedPatient);

  const { data: prescriptions, isLoading: isLoadingPrescriptions } =
    queries.useGetPrescriptions(selectedPatient?.entityId!, {
      enabled: !!selectedPatient?.entityId
    });

  return {
    subPage,
    onTabChanged,
    selectedPatient,
    onSelectPatient,
    patientDetails,
    isLoadingDetails,
    prescriptions,
    isLoadingPrescriptions
  };
};
